import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import toast from 'react-hot-toast';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import PreviousReports from 'components/reports/PreviousReports';
import { LabReport } from 'services/interpret-lab-report/type';
import { downloadFile } from 'lib/utils';
import { Button } from 'components/ui/button';
import Loader from 'components/Loader';
import GenerateLabReport from './GenerateLabReport';
import {
  useDeleteLabReports,
  useGetLabReportDetail,
} from 'services/interpret-lab-report/hooks';
import InputData from '../../components/reports/InputData';
import ReportInsights from 'components/reports/ReportInsigts';
import { useGetHealthReadingById } from 'services/face-scan/hooks';
import { getCategorizedScanData } from 'lib/health-readings';
import useAuthStore from 'store/auth';
import ReportActions from 'components/reports/ReportActions';

interface MobileReportProps {
  reportData: LabReport[];
  setReportData: Dispatch<SetStateAction<LabReport[]>>;
  scrollViewElement: React.ReactNode;
  isLoading: boolean;
}

export default function MobileReport({
  reportData,
  setReportData,
  scrollViewElement,
  isLoading,
}: MobileReportProps) {
  const [selectedTokenId, setSelectedTokenId] = useState<string>('');

  if (selectedTokenId) {
    return (
      <ReportDetail
        tokenId={selectedTokenId}
        onBack={() => setSelectedTokenId('')}
      />
    );
  }

  return (
    <div className='col-span-4 px-6 pt-4 lg:hidden'>
      <div className='space-y-4'>
        <GenerateLabReport />

        <PreviousReports
          reportData={reportData}
          tokenId={selectedTokenId}
          setTokenId={setSelectedTokenId}
          setReportData={setReportData}
          scrollViewElement={scrollViewElement}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

function ReportDetail({
  tokenId,
  onBack,
}: {
  tokenId: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const { data: reportDetail, isFetching } = useGetLabReportDetail({
    tokenId,
  });

  const { data: healthReadings, isFetching: isReadingFetching } =
    useGetHealthReadingById({
      readingId: reportDetail?.[0]?.payload?.scan_data || '',
    });

  const { mutate: deleteReports } = useDeleteLabReports({
    onMutate: () => setIsDeleting(true),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['inifinite-lab-report', profileId],
      });
      setIsDeleting(false);
      toast.success(t('report_deleted_msg'));
      onBack();
    },
    onError: () => setIsDeleting(false),
  });

  const categorizedScanData = useMemo(
    () => getCategorizedScanData({ healthReadings, selectedIndex: 0 }),
    [healthReadings]
  );

  const onDownload = async (url: string | undefined) => {
    setIsDownloading(true);
    await downloadFile(url);
    setIsDownloading(false);
  };

  if (isFetching || isReadingFetching) {
    return <Loader />;
  }

  return (
    <div className='px-6 pt-4 sm:px-14 lg:hidden'>
      <div className='flex justify-end'>
        <Button
          variant='ghost'
          className='text-sm text-blue-500 hover:text-blue-500 h-fit'
          onClick={onBack}
        >
          <ChevronLeft className='w-4 h-4 mr-2' /> {t('previous_reports')}
        </Button>
      </div>

      <ReportActions
        content={reportDetail?.[0]?.report_link || ''}
        isDownloading={isDownloading}
        isDeleting={isDeleting}
        onDelete={() => deleteReports([tokenId])}
        onDownload={() => onDownload(reportDetail?.[0]?.report_link)}
        disabled={!reportDetail?.length}
      />

      <Accordion type='multiple' className='relative mt-4'>
        <AccordionItem value={t('insights')}>
          <AccordionTrigger
            className='py-2'
            headerClassName='sticky top-0 bg-white z-40'
          >
            {t('insights')}
          </AccordionTrigger>
          <AccordionContent>
            <ReportInsights
              insight={reportDetail?.[0]?.ai_insights}
              scanId={reportDetail?.[0]?.payload?.scan_data || ''}
              reportType='lab'
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value={t('generated_report')}>
          <AccordionTrigger
            className='py-2'
            headerClassName='sticky top-0 bg-white z-40'
          >
            {t('actual_data')}
          </AccordionTrigger>
          <AccordionContent>
            {reportDetail?.[0]?.ai_response
              ? parse(reportDetail[0].ai_response)
              : null}
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value={t('input_parameters')}>
          <AccordionTrigger
            className='py-2'
            headerClassName='sticky top-0 bg-white z-40'
          >
            {t('input_data')}
          </AccordionTrigger>
          <AccordionContent>
            <InputData
              scanData={categorizedScanData}
              questionnaireData={
                reportDetail?.[0]?.payload?.questionnaire || []
              }
              labReportLink={reportDetail?.[0]?.file_link ?? ''}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
